import MaterialBuilder from "../../rendering/material-builder";
import AssetLibrary from "../../assets/asset-library";
import getIndexAttribute from "../../utils/get-index-attribute";

function createBallAndStick(molecules, colorScheme, radii) {
    const bondSize = 0.03125;
    const size = 1024, halfSize = size >> 1, quatSize = halfSize >> 1;
    const meshes = [], geometries = {}, materials = {};
    const tmp = new THREE.Vector3(), tmpMatrix = new THREE.Matrix4();
    const offsetVector = new THREE.Vector3();
    const tmpColor = new THREE.Color();

    const offsetQuaternion = new THREE.Quaternion()
        .setFromAxisAngle(new THREE.Vector3(0, 1, 0), -Math.PI * 0.5);

    molecules
        .map(molecule => {
            const origin = new THREE.Vector3();

            let atomCount = 0;

            molecule.forEach(atom => {
                origin.x += atom.x;
                origin.y += atom.y;
                origin.z += atom.z;
                atomCount++;
            });

            if (atomCount > 0)
                origin.divideScalar(atomCount);

            return { molecule, origin };
        })
        .forEach(({ molecule, origin }) => {
            if (molecule.length === 0) return;

            const bondGeometry = new THREE.BufferGeometry();
            const indices = [], positions = [], normals = [], uvs = [];

            let offsetVertex = 0, offsetIndex = 0;

            const groupedMolecules = molecule.reduce((groups, atom) => {
                const group = groups[atom.elem] = groups[atom.elem] || [];

                group.push(atom);

                return groups;
            }, {});
            
            Object.keys(groupedMolecules).forEach((element, groupIndex) => {
                const group = groupedMolecules[element], groupBeginIndex = offsetIndex;
                const atomColors = element in colorScheme ? colorScheme[element] : colorScheme.default;
                const canvas = document.createElement("canvas"), ctx = canvas.getContext("2d");
                const texture = new THREE.CanvasTexture(
                    canvas,
                    undefined, undefined, undefined,
                    THREE.NearestFilter, THREE.NearestMipMapNearestFilter
                );

                canvas.width = size;
                canvas.height = halfSize;
                ctx.beginPath();
                ctx.fillStyle = `#${tmpColor.setHex(atomColors.atom).getHexString()}`;
                ctx.fillRect(0, 0, size, halfSize);
                ctx.fillStyle = `#${tmpColor.setHex(atomColors.text).getHexString()}`;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.font = `${quatSize >> 1}px Arial`;
                ctx.fillText(element, halfSize, quatSize);

                const { materialIndex, materialAtom: material } = element in materials
                    ? materials[element]
                    : (materials[element] = {
                        materialIndex: groupIndex,
                        materialAtom: MaterialBuilder.build({
                            side: THREE.DoubleSide,
                            envMap: AssetLibrary.envLight.activeMap,
                            map: texture
                        }),
                        materialBond: MaterialBuilder.build({
                            color: atomColors.atom,
                            side: THREE.DoubleSide,
                            envMap: AssetLibrary.envLight.activeMap
                        })
                    });

                group.forEach(atom => {
                    const atomRadii = (atom.elem in radii ? radii[atom.elem] : radii.default) * 0.005;
                    const geometry = atom.elem in geometries
                        ? geometries[atom.elem]
                        : (geometries[atom.elem] = new THREE.SphereBufferGeometry(atomRadii, 32, 32));

                    const atomPos = new THREE.Vector3(atom.x, atom.y, atom.z);

                    atom.bonds.forEach((otherIndex, bondIndex) => {
                        const bondOrder = atom.bondOrder[bondIndex];
                        const other = molecule[otherIndex];

                        tmp.set(other.x, other.y, other.z);
                        tmpMatrix.lookAt(tmp, atomPos, THREE.Object3D.DefaultUp);

                        function createHalfBond(radiusBond, bondLen, isOpen) {
                            const geometryCylinder = new THREE.CylinderBufferGeometry(radiusBond, radiusBond, bondLen, 32, 1, isOpen)
                                .translate(0, bondLen * 0.5, 0)
                                .rotateX(Math.PI * 0.5)
                                .applyMatrix(tmpMatrix);

                            const attrCylPosition = geometryCylinder.getAttribute("position");
                            const attrCylNormal = geometryCylinder.getAttribute("normal");
                            const attrCylUv = geometryCylinder.getAttribute("uv");
                            const attrCylIndices = geometryCylinder.index;
                            const countIndex = attrCylIndices.count;
                            const countVertex = attrCylPosition.count;

                            for (let i = -Math.floor(bondOrder * 0.5), j = Math.floor(bondOrder * 0.5); i <= j; i++) {
                                if (i === 0 && (bondOrder & 1) === 0) continue;

                                const offset = i * bondSize * bondOrder;
                                offsetVector.set(offset, 0, 0).applyMatrix4(tmpMatrix);

                                for (let bi = 0; bi < countIndex; bi++)
                                    indices.push(attrCylIndices.array[bi] + offsetVertex);

                                for (let bi = 0; bi < countVertex; bi++) {
                                    const inormal = bi * attrCylNormal.itemSize;
                                    const iuv = bi * attrCylUv.itemSize;

                                    tmp
                                        .fromArray(attrCylPosition.array, attrCylPosition.itemSize * bi)
                                        .add(offsetVector)
                                        .add(atomPos);

                                    positions.push(tmp.x, tmp.y, tmp.z);

                                    normals.push(attrCylNormal.array[inormal], attrCylNormal.array[inormal + 1], attrCylNormal.array[inormal + 2]);
                                    uvs.push(attrCylUv.array[iuv], attrCylUv.array[iuv + 1]);
                                }

                                offsetVertex = offsetVertex + countVertex;
                                offsetIndex = offsetIndex + countIndex;
                            }
                        }

                        createHalfBond(bondSize, atomPos.distanceTo(tmp) * 0.5, true);
                        createHalfBond(bondSize + atomRadii * 0.125 / bondOrder, atomRadii * 1.05, false);
                    });

                    bondGeometry.addGroup(groupBeginIndex, offsetIndex - groupBeginIndex, materialIndex);

                    tmp.subVectors(origin, atomPos).sub(atomPos);

                    tmpMatrix.lookAt(atomPos, tmp, THREE.Object3D.DefaultUp);

                    const atomQuat = new THREE.Quaternion()
                        .setFromRotationMatrix(tmpMatrix)
                        .multiply(offsetQuaternion);

                    meshes.push({ geometry, material, name: atom.elem, position: atomPos, quaternion: atomQuat });
                });
            });

            const ConstructorIndex = getIndexAttribute(indices.length);

            const attrPosition = new THREE.Float32BufferAttribute(positions, 3);
            const attrNormal = new THREE.Float32BufferAttribute(normals, 3);
            const attrUv = new THREE.Float32BufferAttribute(uvs, 2);
            const attrIndices = new ConstructorIndex(indices, 1);

            bondGeometry.setIndex(attrIndices);
            bondGeometry.addAttribute("position", attrPosition);
            bondGeometry.addAttribute("normal", attrNormal);
            bondGeometry.addAttribute("uv", attrUv);

            const material = Object
                .values(materials)
                .sort((a, b) => a.materialIndex - b.materialIndex)
                .map(({ materialBond: material }) => material);

            meshes.push({ geometry: bondGeometry, material, name: "Bonds" });
        });

    return meshes;
}

export default createBallAndStick;
export { createBallAndStick };